import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { FaTimes } from 'react-icons/fa';
import Modal, { Field, Label } from '../../../../../../common/Modal';
import { projectsApi } from '../../../../../../../services/api';
import { useAlert } from '../../../../../../../contexts/AlertContext';
import { toPickerValue, fromPickerValue } from '../../../../../../../utils/dateOnly';

/**
 * Modal for ending an employee's project allocation
 * 
 * @param {Object} props
 * @param {Boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Object} props.employee - The employee whose project is being ended
 * @param {Function} props.onSuccess - Callback when project end is successful
 */
const EndProjectModal = ({
  isOpen,
  onClose,
  employee,
  onSuccess
}) => {
  const { showAlert } = useAlert();
  const [endDate, setEndDate] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setEndDate(null);
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      
      await projectsApi.endEmployeeProject(employee.employeeProjectId, {
        endDate: fromPickerValue(endDate) ?? undefined
      });
      
      showAlert('Project ended successfully', 'success');
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error ending project:', error);
      showAlert(error.message || 'Error ending project', 'error', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  if (!employee) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="End Project"
      onSubmit={handleSubmit}
      submitText={isSubmitting ? 'Ending Project...' : 'End Project'}
      submitDisabled={!endDate || isSubmitting}
      submitButtonProps={{
        sx: { backgroundColor: '#2563EB', color: '#FFFFFF', '&:hover': { backgroundColor: '#1E40AF' } }
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Field>
          <Typography variant="subtitle1" gutterBottom>
            Project End Date
          </Typography>
          <DatePicker
            value={endDate}
            onChange={(newValue) => {
              if (newValue && typeof newValue.isValid === 'function' && newValue.isValid()) {
                setEndDate(newValue);
              } else if (newValue === null) {
                setEndDate(null);
              }
            }}
            minDate={toPickerValue(employee?.projectStartDate)}
            format="DD/MM/YYYY"
            disabled={isSubmitting}
            slotProps={{
              textField: {
                fullWidth: true,
                placeholder: "When will the individual end on this project?",
                size: "medium",
                required: true,
                inputProps: {
                  placeholder: 'DD/MM/YYYY'
                }
              }
            }}
          />
          {endDate && (
            <Box sx={{ mt: 1 }}>
              <Typography
                variant="body2"
                color="primary"
                sx={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                onClick={() => setEndDate(null)}
              >
                <FaTimes size={12} /> Clear date
              </Typography>
            </Box>
          )}
        </Field>
      </Box>
    </Modal>
  );
};

export default EndProjectModal;
